import { useLocales } from "@/locales";
import { CommonWrap } from "@/PageComponents/common";
import ContentWrap from "@/DocLayout/Content/Index";
import { Box, Link } from "@mui/material";
import { Trans } from "react-i18next";
import ShowTable from "@/DocLayout/Table/index";
import { ADMIN_DEV_URL } from "@/config";
import CodeSnippet from "@/components/CodeSnippet";
import RespTempImg from "@/assets/img/webhook-use/resp-temp.png";
import Image from "@/components/image";
import { useContext } from "react";
import { ChooseCodeLanguageContext } from "@/components/ChooseCodeLanguage/ChooseCodeLanguage";

export default function WebhookNotificationSteps() {
  const { translate, currentLang } = useLocales();
  const { language } = useContext(ChooseCodeLanguageContext);
  const codeMap: { [key: string]: any } = {
    javascript: {
      temp_code: `const express = require('express');
const crypto = require('crypto');

const app = express();
const port = 3000;

app.use(express.json());

app.post('/webhook', (req, res) => {
  const { headers, body } = req;

  const appId ='*** your appId ***'; //  Your APP ID
  const appSecret ='*** your appSecret ***'; //  Your APP Secret

  const timestamp = headers['timestamp'];
  const signature = headers['sign'];

  const bodyText = JSON.stringify(body).replace(/s/g, '');

  if (verifySignature(bodyText, signature, appId, appSecret, timestamp)) {
    res.status(200).send('success');
  } else {
    res.status(400).send('Invalid signature');
  }
});

function verifySignature(bodyText, signature, appId, appSecret, timestamp) {
  const signText = appId + timestamp + bodyText;
  const serverSign = crypto
    .createHmac('sha256', appSecret)
    .update(signText)
    .digest('hex');

  return signature === serverSign;
}

app.listen(port, () => {
  console.log(${`Server running on port \${port}`});
});`,
      resp_example_code: ``,
    },
    python: {
      temp_code: `# Python FastAPI
import hashlib
import hmac
import json
import binascii
from fastapi import FastAPI, HTTPException, Request
from pydantic import BaseModel
from fastapi.responses import PlainTextResponse
app = FastAPI()
def verify_signature(content: str, signature: str, app_id: str, app_secret: str, timestamp: str):
    sign_text = f"{app_id}{timestamp}{content}"
    h = hmac.new(app_secret.encode('utf-8'), sign_text.encode('utf-8'), hashlib.sha256)
    server_sign = binascii.hexlify(h.digest()).decode('utf-8')
    if signature == server_sign:
        return True
    else:
        return False
class Webhook(BaseModel):
    type: str
    msg: dict
@app.post("/webhook", response_class=PlainTextResponse)
def process_webhook(item: Webhook, request: Request):
    app_id = "*** your app_id ***"
    app_secret = "*** your app_secret ***"
    timestamp = request.headers.get("Timestamp")  
    sign = request.headers.get("Sign")  
    sign_text = json.dumps(dict(item)).replace(" ", "")
    if verify_signature(content=sign_text, signature=sign, app_id=app_id, app_secret=app_secret, timestamp=timestamp):
        return "success"
    else:
        raise HTTPException(status_code=400, detail="Invalid signature")`,
      resp_example_code: ``,
    },
    go: {
      temp_code: `package main

import (
        "crypto/hmac"
        "crypto/sha256"
        "encoding/hex"
        "fmt"
        "log"
        "net/http"
        "strings"
        "time"

        "github.com/gin-gonic/gin"
)

type Webhook struct {
        Type string      ${`json:"type"`}
        Msg  interface{} ${`json:"msg"`}
}

func main() {
        router := gin.Default()

        router.POST("/webhook", processWebhook)

        if err := router.Run(":8080"); err != nil {
                log.Fatal(err)
        }
}

func processWebhook(c *gin.Context) {
        var webhook Webhook
        if err := c.ShouldBindJSON(&webhook); err != nil {
                c.JSON(http.StatusBadRequest, gin.H{"error": "Invalid JSON payload"})
                return
        }

        appId := "*** your appId ***"
        appSecret := "*** your appSecret ***"
        timestamp := c.GetHeader("Timestamp")
        signature := c.GetHeader("Sign")

        signText := strings.ReplaceAll(fmt.Sprintf("%v", webhook), " ", "")
        if verifySignature(signText, signature, appID, appSecret, timestamp) {
                c.String(http.StatusOK, "success")
        } else {
                c.String(http.StatusBadRequest, "Invalid signature")
        }
}

func verifySignature(content, signature, appID, appSecret, timestamp string) bool {
        signText := appID + timestamp + content
        serverSign := hmacSha256(signText, appSecret)
        return signature == serverSign
}

func hmacSha256(data, key string) string {
        h := hmac.New(sha256.New, []byte(key))
        _, err := h.Write([]byte(data))
        if err != nil {
                log.Fatal(err)
        }
        return hex.EncodeToString(h.Sum(nil))
}`,
      resp_example_code: ``,
    },
    php: {
      temp_code: `<?php

use PsrHttpMessageResponseInterface as Response;
use PsrHttpMessageServerRequestInterface as Request;
use SlimFactoryAppFactory;

require __DIR__ . '/vendor/autoload.php';

class Webhook {
    public $type;
    public $msg;
}

$app = AppFactory::create();

$app->addRoutingMiddleware();

$app->post('/webhook', function (Request $request, Response $response) {
    $body = $request->getBody()->getContents();
    $headers = $request->getHeaders();

    $appID = "*** your appID ***";
    $appSecret = "*** your appSecret ***";

    $timestamp = $headers['Timestamp'][0];
    $signature = $headers['Sign'][0];

    $webhook = json_decode($body, true);
    $signText = str_replace(" ", "", json_encode($webhook));

    if (verifySignature($signText, $signature, $appID, $appSecret, $timestamp)) {
        $response->getBody()->write('success');
    } else {
        $response = $response->withStatus(400)->withBody(new SlimPsr7Stream(fopen('php://temp', 'r+')));
        $response->getBody()->write('Invalid signature');
    }

    return $response;
});

function verifySignature($content, $signature, $appID, $appSecret, $timestamp) {
    $signText = $appID . $timestamp . $content;
    $serverSign = hash_hmac('sha256', $signText, $appSecret);

    return $signature === $serverSign;
}

$app->run();`,
      resp_example_code: ``,
    },
    java: {
      temp_code: `import org.springframework.boot.SpringApplication;
import org.springframework.boot.autoconfigure.SpringBootApplication;
import org.springframework.http.HttpHeaders;
import org.springframework.http.HttpStatus;
import org.springframework.http.ResponseEntity;
import org.springframework.web.bind.annotation.PostMapping;
import org.springframework.web.bind.annotation.RequestBody;
import org.springframework.web.bind.annotation.RequestHeader;
import org.springframework.web.bind.annotation.RestController;

import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import java.nio.charset.StandardCharsets;
import java.security.InvalidKeyException;
import java.security.NoSuchAlgorithmException;
import java.time.Instant;

@SpringBootApplication
@RestController
public class WebhookApplication {

    public static void main(String[] args) {
        SpringApplication.run(WebhookApplication.class, args);
    }

    @PostMapping("/webhook")
    public ResponseEntity<String> processWebhook(@RequestBody Webhook webhook,
                                                  @RequestHeader("Timestamp") String timestamp,
                                                  @RequestHeader("Sign") String signature) {
        String appId = "*** your appId ***";
        String appSecret = "*** your appSecret ***";

        String signText = webhook.toString().replace(" ", "");
        if (verifySignature(signText, signature, appId, appSecret, timestamp)) {
            return ResponseEntity.ok("success");
        } else {
            return ResponseEntity.status(HttpStatus.BAD_REQUEST).body("Invalid signature");
        }
    }

    private boolean verifySignature(String content, String signature, String appId, String appSecret, String timestamp) {
        String signText = appId + timestamp + content;
        String serverSign = hmacSha256(signText, appSecret);
        return signature.equals(serverSign);
    }

    private String hmacSha256(String data, String key) {
        try {
            Mac hmac = Mac.getInstance("HmacSHA256");
            SecretKeySpec secretKey = new SecretKeySpec(key.getBytes(StandardCharsets.UTF_8), "HmacSHA256");
            hmac.init(secretKey);
            byte[] digest = hmac.doFinal(data.getBytes(StandardCharsets.UTF_8));
            return bytesToHex(digest);
        } catch (NoSuchAlgorithmException | InvalidKeyException e) {
            e.printStackTrace();
            return null;
        }
    }

    private String bytesToHex(byte[] bytes) {
        StringBuilder result = new StringBuilder();
        for (byte b : bytes) {
            result.append(String.format("%02x", b));
        }
        return result.toString();
    }

    static class Webhook {
        private String type;
        private Object msg;

        // Getters and setters

        @Override
        public String toString() {
            return "Webhook{" +
                    "type='" + type + ''' +
                    ", msg=" + msg +
                    '}';
        }
    }
}`,
      resp_example_code: ``,
    },
  };

  return (
    <ContentWrap
      showBorderBottom={false}
      leftChildrenSx={{
        paddingTop: "1px",
        paddingBottom: "1px",
      }}
      leftChildren={
        <CommonWrap>
          <>
            <Box
              id="how-to-handle-incoming-webhooks"
              component={"div"}
              className="title_0"
            >
              {translate("webhook_use.notifiy_step_title")}
            </Box>

            <Box component={"div"} className="title_1">
              {translate("webhook_use.notifiy_step_list1_title")}
            </Box>

            <Box
              sx={{
                ul: {
                  paddingLeft: "2em",
                },
                li: {
                  "::marker": {
                    textIndent: "2em",
                  },
                },
              }}
            >
              <ul>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc1`)}
                  </Box>
                  <ShowTable
                    tableData={[
                      {
                        name: "Appid",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: (
                          <Trans
                            i18nKey={
                              "quick_start.title5_desc1_table_app_id_desc"
                            }
                            t={translate as any}
                          >
                            Just a placeholder
                            <Link
                              href={ADMIN_DEV_URL}
                              target="_blank"
                              className="link"
                              underline="always"
                            >
                              Just a placeholder
                            </Link>
                          </Trans>
                        ),
                      },
                      {
                        name: "Timestamp",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: `${translate(
                          "quick_start.title5_desc1_table_timestamp_desc"
                        )}`,
                      },
                      {
                        name: "Sign",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: `${translate(
                          "quick_start.title5_desc1_table_sgin_desc"
                        )}`,
                      },
                    ]}
                  />
                </li>
                <li>
                  <Box component={"div"} className="desc" mt={1}>
                    <Trans
                      i18nKey={"webhook_use.notifiy_step_list1_desc2"}
                      t={translate as any}
                    >
                      Just a placeholder
                      <a
                        href={"#api-authentication-and-specifications"}
                        className="link"
                      >
                        Just a placeholder
                      </a>
                    </Trans>
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc3`)}
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc4`)}
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc5`)}
                  </Box>
                </li>
              </ul>
            </Box>

            {currentLang.value === "en" ? (
              <Box component={"div"} className="desc">
                <Trans
                  i18nKey={"webhook_use.notifiy_step_list2_title"}
                  t={translate as any}
                >
                  s<strong></strong>
                  <Link underline="always"></Link>
                </Trans>
              </Box>
            ) : (
              <Box component={"div"} className="title_1">
                {translate("webhook_use.notifiy_step_list2_title")}
              </Box>
            )}

            {currentLang.value === "en" ? (
              <Box component={"div"} className="desc">
                <Trans
                  i18nKey={"webhook_use.notifiy_step_list3_title"}
                  t={translate as any}
                >
                  s<strong></strong>
                </Trans>
              </Box>
            ) : (
              <Box component={"div"} className="title_1">
                {translate("webhook_use.notifiy_step_list3_title")}
              </Box>
            )}

            <Image src={RespTempImg} />
          </>
        </CommonWrap>
      }
      rightChildren={
        <>
          <CodeSnippet
            title={translate("common.sign_example")}
            code={codeMap[language].temp_code}
          />
          {/* <Box mb={3} /> */}
          {/* <CodeSnippet
            title={translate("common.resp_example")}
            code={codeMap[language].resp_example_code}
          /> */}
        </>
      }
    />
  );
}
